import React, { useEffect, useState } from 'react';

// Guida al collegamento di una colonnina al gateway OCPP, nella pagina Colonnine.
// Aperta se la società non ha ancora colonnine, chiusa altrimenti: chi la
// conosce la riapre quando serve.

const HOST_GATEWAY = 'ocpp.pianinvest.it';

export default function GuidaCollegamento({ apertaIniziale }: { apertaIniziale: boolean }) {
  const [aperta, setAperta] = useState(apertaIniziale);
  useEffect(() => { setAperta(apertaIniziale); }, [apertaIniziale]);

  return (
    <div className="bop-card">
      <div className="bop-card__header">
        <h3 className="bop-card__title">Come collegare una colonnina al gateway</h3>
        <button className="bop-btn bop-btn-secondary bop-btn-sm" onClick={() => setAperta(!aperta)}>
          {aperta ? 'Nascondi la guida' : 'Mostra la guida'}
        </button>
      </div>
      {aperta && (
        <div className="bop-card__body bop-ricarica__guida">
          <p className="bop-ricarica__nota">
            Le colonnine parlano con BOP attraverso il gateway OCPP del gruppo, all'indirizzo{' '}
            <code className="bop-ricarica__codice">wss://{HOST_GATEWAY}</code>. Servono colonnine che
            supportano <strong>OCPP 1.6J</strong> (JSON su WebSocket) con collegamento sicuro e password.
          </p>

          <h4 className="bop-ricarica__guida-titolo">1. Prima, qui in BOP</h4>
          <ol className="bop-ricarica__guida-passi">
            <li className="bop-ricarica__guida-passo">
              <strong>Tariffa</strong>: la società deve avere una tariffa in vigore oggi. Si inserisce dalla
              scheda di una colonnina, riquadro Tariffe. Senza tariffa non si può generare la password.
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Colonnina</strong>: crea la colonnina con «+ Nuova colonnina». Il <strong>codice OCPP</strong> è
              l'identificativo che inserirai sulla colonnina: solo lettere maiuscole, numeri, «-» e «_»,
              da 3 a 40 caratteri (es. <code className="bop-ricarica__codice">PIA-SEDE-01</code>). Non si può
              cambiare dopo.
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Password</strong>: nella scheda della colonnina premi «Genera password» e copiala
              subito: viene mostrata una volta sola. Se la perdi, rigenerala e reinseriscila sulla colonnina
              (la vecchia smette di funzionare).
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Badge</strong>: registra nella pagina Badge i badge RFID dei dipendenti che ricaricheranno.
            </li>
          </ol>

          <h4 className="bop-ricarica__guida-titolo">2. Sulla colonnina</h4>
          <p className="bop-ricarica__nota">
            Nel pannello di configurazione della colonnina (app o pagina web del produttore, di solito a cura
            dell'installatore) imposta:
          </p>
          <ol className="bop-ricarica__guida-passi">
            <li className="bop-ricarica__guida-passo">
              <strong>Protocollo</strong>: OCPP 1.6J (non 1.5, non 2.0.1).
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Indirizzo del sistema centrale</strong> (può chiamarsi «Central System URL», «Backend URL»,
              «OCPP server» o «CSMS URL»): <code className="bop-ricarica__codice">wss://{HOST_GATEWAY}</code>.
              Se il campo vuole l'indirizzo completo, aggiungi il codice OCPP in fondo:{' '}
              <code className="bop-ricarica__codice">wss://{HOST_GATEWAY}/PIA-SEDE-01</code>. Vanno bene
              entrambe le forme. Porta 443.
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Identificativo della colonnina</strong> («Charge Point ID», «ChargeBox Identity»,
              «Station ID»): il codice OCPP, scritto identico (maiuscole comprese, senza spazi).
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Sicurezza</strong>: collegamento cifrato (TLS) con password, cioè «Security Profile 2» o
              «Basic Authentication». Utente, se richiesto: il codice OCPP. Password («Authorization Key»,
              «OCPP password»): quella generata in BOP. Non serve caricare certificati: il gateway usa un
              certificato pubblico.
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Autorizzazione</strong>: lettore RFID attivo e «Local Authorization List» abilitata (la
              lista dei badge la manda il gateway). Lascia <strong>disattivata</strong> la ricarica libera
              («Free charging», «Autostart», «Plug &amp; Charge»): le ricariche senza badge non si possono
              addebitare a nessuno.
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Ora</strong>: sincronizzazione automatica dell'orologio (NTP) attiva: inizio e fine delle
              ricariche usano l'ora della colonnina.
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Rete</strong>: la colonnina deve poter uscire su Internet verso{' '}
              <code className="bop-ricarica__codice">{HOST_GATEWAY}</code> sulla porta 443 (meglio via cavo). Se la
              sede ha un firewall, va aperta questa uscita. Non serve aprire porte in entrata.
            </li>
          </ol>
          <p className="bop-ricarica__nota">
            Le letture dell'energia e il loro intervallo (5 minuti) le imposta il gateway da solo a ogni avvio
            della colonnina: lascia i valori del produttore.
          </p>

          <h4 className="bop-ricarica__guida-titolo">3. Verifica</h4>
          <ol className="bop-ricarica__guida-passi">
            <li className="bop-ricarica__guida-passo">
              Entro un minuto la colonnina risulta <strong>Collegata</strong> in questa pagina e nella scheda
              compaiono marca, modello, firmware e lo stato delle prese.
            </li>
            <li className="bop-ricarica__guida-passo">
              Dalla scheda premi «Aggiorna stato»: l'esito deve diventare «Eseguito» in pochi secondi.
            </li>
            <li className="bop-ricarica__guida-passo">
              Fai una ricarica di prova con un badge registrato: compare in Sessioni come «In corso» e, alla
              fine, «Conclusa» con energia e importo. Confronta i kWh con il contatore della colonnina.
            </li>
            <li className="bop-ricarica__guida-passo">
              Un badge non registrato viene rifiutato e compare nella pagina Badge, fra i badge sconosciuti, da
              dove si abilita.
            </li>
          </ol>

          <h4 className="bop-ricarica__guida-titolo">Se non si collega</h4>
          <ul className="bop-ricarica__guida-passi">
            <li className="bop-ricarica__guida-passo">
              <strong>Resta «Non collegata»</strong>: ricontrolla indirizzo, identificativo (maiuscole, spazi) e
              password; se hai dubbi sulla password, rigenerala e reinseriscila. Verifica che la colonnina
              raggiunga Internet sulla porta 443.
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>«Password da generare»</strong>: la password non è ancora stata creata (serve prima una
              tariffa in vigore).
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>«Disattivata»</strong>: una colonnina disattivata viene scollegata; riattivala dalla scheda.
            </li>
            <li className="bop-ricarica__guida-passo">
              <strong>Riquadro rosso «Gateway non raggiungibile»</strong>: il problema non è la colonnina ma il
              server del gateway; avvisa l'amministratore di BOP. Nel frattempo le colonnine già configurate
              continuano a ricaricare con i badge che hanno in memoria.
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
